import { RootfiCompanyUseCase, RootfiSyncStatus } from '@commenda/prisma';
import { SWRFetcher } from '@commenda/utils';
import useSWR, { KeyedMutator } from 'swr';

import { RootfiSyncStatusResponse } from './types';

/** While any entity is mid-sync the statuses are re-read this often, so a resync's progress shows without a reload. */
const SYNCING_REFRESH_INTERVAL_MS = 5_000;

const fetchSyncStatus = SWRFetcher({ method: 'GET' });

/** The ERP sync status of several entities at once, flattened into one list. */
export function useRootfiSyncStatusesSWR(
  corporationIds: readonly number[],
  useCase: RootfiCompanyUseCase
) {
  const key = corporationIds.length
    ? ['rootfi/sync-status', useCase, ...corporationIds]
    : null;

  const {
    data,
    error,
    mutate,
    isLoading,
  }: {
    data?: RootfiSyncStatusResponse[][];
    error?: unknown;
    mutate: KeyedMutator<RootfiSyncStatusResponse[][]>;
    isLoading: boolean;
  } = useSWR(
    key,
    () =>
      Promise.all(
        corporationIds.map((corporationId) =>
          fetchSyncStatus(
            `rootfi/sync-status/${corporationId}?useCase=${useCase}`
          )
        )
      ),
    {
      refreshInterval: (latest?: RootfiSyncStatusResponse[][]) =>
        latest?.some((statuses) =>
          statuses.some(
            (syncStatus) => syncStatus.status === RootfiSyncStatus.IN_PROGRESS
          )
        )
          ? SYNCING_REFRESH_INTERVAL_MS
          : 0,
    }
  );

  return {
    data: data?.flat(),
    error,
    mutate,
    isLoading,
  };
}
